import type { ChangeEventHandler } from 'react'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import OfferAirportSelectBox from '../Airport/OfferAirportSelectBox'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import CharterFlightOfferAirportOptionsList from './CharterFlightOfferAirportOptionsList'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { airportLocationToAirport } from 'lib/flights/flightUtils'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import FloatingTooltip from 'LuxKit/FloatingTooltip'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
const DropdownContainer = styled.div`
  position: relative;
`

interface Props {
  open: boolean
  selectedAirport?: App.Airport
  className?: string
}

function CharterFlightOfferAirportSelect({ open, selectedAirport, className }: Props) {
  const intl = useIntl()
  const trigger = useRef<HTMLButtonElement>(null)
  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const isMobile = useScreenSizeOnly('mobile')
  const [searchPhrase, setSearchPhrase] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const closeFilter = useCallback(() => {
    dispatch({ type: OfferPageActions.closeFilter })
    trackEvent('AIRPORT_FILTER_CLOSED')
  }, [dispatch, trackEvent])

  const onAirportSelect = useCallback((airport: App.AirportLocation) => {
    dispatch({
      type: OfferPageActions.setAirport,
      airport: airportLocationToAirport(airport),
    })
    dispatch({
      type: OfferPageActions.setFlights,
      flights: true,
    })
    trackEvent('AIRPORT_FILTER_SELECTED')
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch, trackEvent])

  useEffect(() => {
    if (open && !isMobile) {
      inputRef.current?.focus()
    }
  }, [isMobile, open])

  const handleSearchPhraseInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setSearchPhrase(event.target.value)
  }, [])

  return (
    <DropdownContainer className={className}>
      <OfferAirportSelectBox
        ref={trigger}
        disabled={isAgentHubEnabled}
        style={isAgentHubEnabled ? { cursor: 'not-allowed' } : undefined}
      />
      {!isAgentHubEnabled && (
        <DropdownList
          size="M"
          open={open && !isMobile}
          anchorRef={trigger}
          triggerRef={trigger}
          title={intl.formatMessage({
            id: 'CA00xRlKYLWOjfCK9oWE',
            defaultMessage: 'Where will you depart from?',
            description: 'Title for airport selection dropdown on offer page',
          })}
          subtitle={intl.formatMessage({
            id: 'irUuThVkPLQ6NWXtug86',
            defaultMessage: 'Select your preferred airport',
            description: 'Subtitle for airport selection dropdown on offer page',
          })}
          onClose={closeFilter}
          headerExtension={<TextInputOnly
            placeholder={intl.formatMessage({
              id: 'MSBSsIGoyeyYxNxDTSCn',
              defaultMessage: 'Search by city or airport',
              description: 'Placeholder text for airport search input on offer page',
            })}
            noValidationMessage
            noValidationSpacing
            value={searchPhrase}
            onChange={handleSearchPhraseInputChange}
            endIcon={<LineSearchIcon />}
            ref={inputRef}
          />}
        >
          <CharterFlightOfferAirportOptionsList
            selectedAirport={selectedAirport}
            onSelectAirport={onAirportSelect}
            searchPhrase={searchPhrase}
          />
        </DropdownList>
      )}
      {isAgentHubEnabled && selectedAirport && (
        <FloatingTooltip
          triggerRef={trigger}
          description={
            <Group direction="vertical" gap={8}>
              <BodyText variant="small" weight="normal" as="span">
                {intl.formatMessage({
                  id: 'se2ZMLzQk0mlqsnCsLTv',
                  defaultMessage: 'This flight only departs from {airportName} ({airportCode}) - any flights to or from {airportCode} must be purchased separately.',
                  description: 'Warning about charter flight departures and separate flight purchases',
                }, { airportName: selectedAirport.name, airportCode: selectedAirport.code })}
              </BodyText>
              <BodyText variant="small" weight="normal" as="span">
                {intl.formatMessage({
                  id: 'f5sFGvbGmTo9EMjtJF78',
                  defaultMessage: 'Please ensure you leave enough time for transfers - you are responsible for arriving for this flight on time. Missed flights will not be refunded.',
                  description: 'Warning about transfer times and responsibility for charter flight departures',
                })}
              </BodyText>
            </Group>
          }
          placement="bottom"
          portaled
        />
      )}
    </DropdownContainer>
  )
}

export default withOfferPageState(state => ({
  open: state.activeFilter === 'airport',
  selectedAirport: state.airport,
}), CharterFlightOfferAirportSelect)
